import { forwardRef, useMemo, type FormEvent, type KeyboardEvent } from "react";
import { useCombobox } from "downshift";
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";
import { ComposerActionButton } from "./ComposerActionButton";
import { matchQuestions } from "./questionCatalog";

interface ComposerProps {
  /** "centered" = empty-state hero composer; "docked" = sticky-bottom bar. */
  placement: "centered" | "docked";
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: (e: FormEvent) => void;
  /** True while the agent is generating — locks the input + shows Stop. */
  streaming: boolean;
  onStop: () => void;
  clientName: string;
  /** Called when the input gains focus — lets a scroll-hidden bar reveal itself
   *  so keyboard users tabbing in aren't left interacting with a hidden field. */
  onFocus?: () => void;
}

/**
 * The one chat composer, rendered both centered (empty state) and docked
 * (sticky bottom). A single `<input>` with an in-bar action button morphing
 * Send↔Stop, plus a WAI-ARIA combobox type-ahead (downshift) offering curated
 * question completions as the user types.
 *
 * While streaming the input is read-only and dimmed — one clear "running"
 * state — and the button is Stop. The button is part of the bar (absolute-
 * right), so the input reserves right padding for it.
 *
 * Type-ahead never fights typing: suggestions appear only with ≥2 chars and a
 * match, nothing is auto-highlighted, and Enter submits the typed text unless
 * the user has arrowed onto a suggestion (then Enter fills it; a second Enter
 * sends). The menu opens downward when centered, upward when docked.
 */
export const Composer = forwardRef<HTMLInputElement, ComposerProps>(function Composer(
  { placement, draft, onDraftChange, onSubmit, streaming, onStop, clientName, onFocus },
  ref,
) {
  const placeholder =
    placement === "centered"
      ? `Ask anything about ${clientName || "the portfolio"}`
      : clientName
        ? `Ask about ${clientName}'s portfolio — e.g. "How many stores?"`
        : 'Ask about the portfolio — e.g. "How many stores?"';

  // Suggested completions — static catalog, suppressed while streaming.
  const items = useMemo(
    () => (streaming ? [] : matchQuestions(draft)),
    [draft, streaming],
  );

  const { isOpen, highlightedIndex, getMenuProps, getInputProps, getItemProps } = useCombobox({
    items,
    inputValue: draft,
    // Keep selection unpersisted so the same suggestion can be re-picked and so
    // selecting only ever fills the draft (the user then sends explicitly).
    selectedItem: null,
    onInputValueChange: ({ inputValue }) => onDraftChange(inputValue ?? ""),
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) onDraftChange(selectedItem);
    },
    stateReducer(_state, { changes }) {
      // Never open with nothing to show — keeps aria-expanded honest and avoids
      // an empty popup box. Decide from the *incoming* input value (changes),
      // not the closed-over `items`, which lags one keystroke behind and would
      // otherwise keep the menu shut on the keystroke that crosses the 2-char
      // match threshold.
      if (changes.isOpen && matchQuestions(changes.inputValue ?? draft).length === 0) {
        return { ...changes, isOpen: false };
      }
      return changes;
    },
  });

  // Docked composer sits at the viewport bottom → open the menu UPWARD; the
  // centered composer has room below → open DOWNWARD.
  const openUp = placement === "docked";
  const menuVisible = isOpen && items.length > 0;

  return (
    <form onSubmit={onSubmit} className="relative" aria-label="Ask the dashboard agent">
      <Input
        {...getInputProps({
          ref,
          placeholder,
          // Downshift would otherwise point aria-labelledby at a generated label
          // id that is never rendered, leaving the input with no accessible name.
          "aria-label": "Ask the dashboard agent",
          readOnly: streaming,
          "aria-disabled": streaming || undefined,
          autoComplete: "off",
          spellCheck: false,
          onFocus,
          className: cn(
            "h-14 w-full rounded-xl bg-card pr-16 !text-[18px]",
            streaming && "opacity-60",
          ),
          onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
            // While streaming the input is read-only; swallow Enter so it can't
            // re-submit the still-populated draft and abort/restart the
            // in-flight stream (the in-bar button is Stop, not Send, mid-run).
            if (event.key === "Enter" && streaming) {
              event.preventDefault();
              return;
            }
            // No suggestion highlighted → Enter sends the typed text. Tell
            // downshift to skip its Enter handling so the form submit fires.
            if (event.key === "Enter" && highlightedIndex < 0) {
              (
                event.nativeEvent as unknown as { preventDownshiftDefault?: boolean }
              ).preventDownshiftDefault = true;
            }
          },
        })}
      />
      <ComposerActionButton
        streaming={streaming}
        canSubmit={draft.trim().length > 0}
        onStop={onStop}
      />
      {/* getMenuProps must always render (it carries the listbox ref); items
          render only when there's something to show. */}
      <ul
        {...getMenuProps()}
        className={cn(
          "absolute inset-x-0 z-50 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg",
          openUp ? "bottom-full mb-2" : "top-full mt-2",
          menuVisible ? "max-h-[280px] overflow-y-auto py-1" : "hidden",
        )}
      >
        {menuVisible
          ? items.map((item, index) => (
              <li
                key={`${item}-${index}`}
                {...getItemProps({ item, index })}
                className={cn(
                  "cursor-pointer px-4 py-2.5 text-[15px] leading-snug transition-colors",
                  highlightedIndex === index
                    ? "bg-accent text-accent-foreground"
                    : "text-popover-foreground/90",
                )}
              >
                {item}
              </li>
            ))
          : null}
      </ul>
    </form>
  );
});
